import { useRef, useState } from "react";
import { Popover, Whisper } from "rsuite";

import { Button } from "reactstrap";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import { View } from "../../types/PageInterfaces";
import useDuplicatePage from "../../api/api-hooks/pages/useDuplicatePage";
import { headerOpenAtom, pageSettingsAtom } from "../../atoms/PageAtoms";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import usePagePermissions from "../../hooks/usePagePermissions";
import PopoverDropdownMenu from "../Menus/PopoverDropdownMenu";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import DropdownMenuDivider from "../Menus/DropdownMenuDivider";
import { Page } from "../../types/GlobalInterfaces";
import { editDashboardAtom } from "../../atoms/ViewAtoms";
import usePage from "../../api/api-hooks/pages/usePage";
import ShareScreenDrawer from "./ShareScreenDrawer/ShareScreenDrawer";
import IconButton from "../../elements/IconButton";
import { IsMobileAtom } from "../../App";

export const HeaderOptions = ({ page }: { page: Page }) => {
  const { duplicatePage } = useDuplicatePage();
  const setSelectedPage = useSetAtom(pageSettingsAtom);
  const whisperRef = useRef<any>();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions(page?.id);
  const [sharePage, setSharePage] = useState(false);
  const isGlobal = !!page?.GlobalAppID;

  const Options = () => (
    <PopoverDropdownMenu>
      <DropdownOptionWithIcon
        name="Share Page"
        onClick={() => setSharePage(true)}
        icon="mdi mdi-share-variant"
      />
      {!isGlobal && (
        <>
          <DropdownOptionWithIcon
            name="Duplicate Page"
            onClick={() => duplicatePage(page.id!)}
            disabled={
              !pPermissions.page_create_duplicate_page &&
              !wPermissions.workspace_duplicate_page &&
              !cPermissions.cloud_create_duplicate_page
            }
            icon="mdi mdi-content-copy"
          />
        </>
      )}

      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Manage Page"
        onClick={() => setSelectedPage(page)}
        disabled={
          !cPermissions.cloud_modify_page_settings &&
          !wPermissions.workspace_modify_page_settings &&
          !pPermissions.page_modify_page_settings
        }
        icon="mdi mdi-cog-outline"
      />
    </PopoverDropdownMenu>
  );

  return (
    <>
      <Whisper
        placement="bottomEnd"
        controlId=""
        trigger="click"
        ref={whisperRef}
        speaker={
          <Popover
            className="sidebar-popover"
            full
            onClick={(e: any) => {
              e.stopPropagation();
              whisperRef.current.close();
            }}
          >
            <Options />
          </Popover>
        }
      >
        <Button size="sm" color="secondary" outline className="btn-icon-only">
          <i
            className="mdi mdi-dots-horizontal cursor-pointer"
            onClick={undefined}
          />
        </Button>
      </Whisper>
      <ShareScreenDrawer
        open={sharePage}
        onClose={() => setSharePage(false)}
        viewID={undefined}
        recordID={undefined}
        recordModule={undefined}
        pageID={page?._id}
        documentID={undefined}
      />
    </>
  );
};

export const Header = ({ page }: { page: Page }) => {
  const [headerOpen] = useAtom(headerOpenAtom);

  return (
    <>
      <h3
        className={"tw:px-2.5 tw:sm:px-0 mb-0 mr-2 text-dark"}
        style={{ whiteSpace: "nowrap" }}
      >
        {page?.Shared && (
          <i className="mdi mdi-share-variant text-primary mr-2" />
        )}
        {page?.Name}
      </h3>
      {headerOpen && page?.Description && (
        <h5 className="d-none d-md-inline text-muted font-weight-500 mb--1">
          {page?.Description || ""}
        </h5>
      )}
    </>
  );
};

export const EditDashboard = ({
  view,
  showInsights,
  toggleInsights,
}: {
  view: View;
  showInsights?: boolean;
  toggleInsights?: () => void;
}) => {
  const [editMode, setEditMode] = useAtom(editDashboardAtom);

  if (view?.Type !== "Dashboard" && !view?.Insights) return null;
  if (view?.Insights && !toggleInsights) return null;

  return (
    <>
      {view?.Insights && toggleInsights && (
        <IconButton
          onClick={toggleInsights}
          icon={`chevron-${showInsights ? "up" : "down"}`}
          tooltipText={showInsights ? "Hide Insights" : "Show Insights"}
        />
      )}
      <Button
        className={`${!editMode ? "btn-icon-only" : ""} ml-auto`}
        size="sm"
        outline={!editMode}
        onClick={() => setEditMode((prev: any) => !prev)}
      >
        {editMode ? "Done" : <i className="mdi mdi-pencil" />}
      </Button>
    </>
  );
};

const PageViewHeader = ({
  pageID,
  view,
}: {
  pageID: string;
  view: View | null;
  showInsights: boolean;
  toggleInsights?: () => void;
}) => {
  const { page, pageLoading } = usePage(pageID);
  const [headerOpen] = useAtom(headerOpenAtom);
  const isMobile = useAtomValue(IsMobileAtom);

  if (pageLoading)
    return (
      <div
        className="skeleton mt-2 mt-sm-3 mb-2"
        style={{ width: 125, height: 30 }}
      ></div>
    );

  if (!headerOpen) return null;

  return (
    <div className="mt-2 mt-sm-3 d-flex justify-content-between align-items-center mb-1">
      <div>
        <Header page={page} />
      </div>
      <div>
        {view && <EditDashboard view={view} />}
        {!isMobile && <HeaderOptions page={page} />}
      </div>
    </div>
  );
};

export default PageViewHeader;
